import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import reactLazyWithRetry from '@fatso83/retry-dynamic-import/react-lazy'
import { PlainLoader } from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useLocation } from 'react-router'
import { useHydrated } from 'remix-utils/use-hydrated'
import { useMediaQuery } from 'usehooks-ts'

import { ApiRenderer } from './ApiRenderer'
import { ExploreButton } from './ExploreButton'
import { OpenAPIRendererToggle } from './OpenAPIRendererToggle'
import { DownloadApiDropdown } from '~/portal/components/ApiViewer/DownloadApiDropdown'
import { GenerateCodeDropdown } from '~/portal/components/ApiViewer/GenerateCodeDropdown'
import { RoutingSettingsButton } from '~/portal/components/ApiViewer/RoutingSettingsButton'
import { BreadcrumbBar } from '~/portal/components/ConsumerBreadcrumbBar'
import { EditContentButtons } from '~/portal/components/ConsumerEditContentButtons'
import useLocalStorage from '~/portal/hooks/useLocalStorage'
import appDefaults from '~/portal/portal.defaults.json'
import { AsyncAPIDefinition, OpenAPI2Definition, OpenAPI3Definition } from '~/portal/types/api.definitions'
import { GeneratorLanguages } from '~/portal/types/code.generator'
import { SHUBApiDetails } from '~/shared/types/api'
import { APIRenderers, OpenAPIRendererState, RoutingSettings } from '~/shared/types/portal'

const ElementsRenderer = reactLazyWithRetry(() => import('./ElementsRenderer').then(comp => ({ default: comp.ElementsRenderer } as any)))

export default function ApiViewer({
  title,
  definition,
  generatorLanguages,
  productId,
  orgName,
  path,
  exploreUrl,
  shubUrl,
  SHUBApiDetails,
  showEditButtons = false,
  renderer,
  lastModifiedDate,
  routingSettings,
  providerContentSlug,
  isConsumerSide,
  flattenAvailable = false,
  registryFlattenAvailable = false,
  tableOfContentsId,
  downloadDisabled = false,
}: {
  title?: string;
  tableOfContentsId?: string;
  definition: OpenAPI2Definition | OpenAPI3Definition | AsyncAPIDefinition;
  generatorLanguages?: GeneratorLanguages,
  productId?: string,
  orgName?: string,
  path?: string,
  exploreUrl?: string,
  shubUrl?: string,
  SHUBApiDetails: SHUBApiDetails,
  showEditButtons?: boolean,
  renderer?: APIRenderers | OpenAPIRendererState,
  lastModifiedDate?: string,
  routingSettings?: RoutingSettings,
  providerContentSlug?: string,
  isConsumerSide?: boolean,
  flattenAvailable?: boolean,
  registryFlattenAvailable?: boolean,
  downloadDisabled?: boolean,
}) {
  const { hash, pathname } = useLocation()

  const isHydrated = useHydrated()

  const isAdmin = useMemo(() => pathname?.toLowerCase()?.includes('/sp-admin/'), [pathname])
  const [ref, setRef] = useState<HTMLDivElement>()

  const containerRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setRef(node)
    }
  }, [])

  const oasVersion = definition.openapi || definition.swagger || ''

  const isOas3_2 = oasVersion.startsWith('3.2')
  const isOas3 = oasVersion.startsWith('3.0')
  const isOas2 = oasVersion.startsWith('2')

  const isAsync = renderer === APIRenderers.ASYNCAPI

  const isMobileScreen = useMediaQuery(appDefaults.media.mobile, { initializeWithValue: false })
  const isMobile = isConsumerSide && isMobileScreen

  const portalFlattenActive = flattenAvailable && (isOas2 || isOas3) && !!productId && !!tableOfContentsId
  const flattenFlow: 'portal' | 'registry' | undefined = portalFlattenActive ? 'portal' : registryFlattenAvailable ? 'registry' : undefined

  const shouldRenderRouting = !isAsync
  const shouldRenderDownload = title && (isAsync || isOas3_2)
  const shouldRenderCodeGen = title && !isAsync && !isOas3_2 && generatorLanguages
  const shouldRenderOpenApiRendererToggle = renderer === OpenAPIRendererState.TOGGLE && isHydrated

  const shouldRenderToolbarDesktop = !isMobile && (shouldRenderRouting || shouldRenderDownload || shouldRenderCodeGen || isOas3 || showEditButtons)
  const shouldRenderToolbarMobile = isMobile && shouldRenderOpenApiRendererToggle
  const shouldRenderToolbar = shouldRenderToolbarDesktop || shouldRenderToolbarMobile

  const rendererRef = useRef<HTMLDivElement>(null)

  const [localRenderer, setLocalRenderer] = useLocalStorage<APIRenderers>('portal.apiViewer.renderer', APIRenderers.ELEMENTS)

  const activeRenderer = useMemo<APIRenderers>(() => {
    if (renderer === OpenAPIRendererState.TOGGLE) {
      return isOas3_2 ? APIRenderers.SWAGGER_UI : localRenderer
    }
    if (renderer === OpenAPIRendererState.SWAGGER_UI) {
      return APIRenderers.SWAGGER_UI
    }
    if (renderer === OpenAPIRendererState.ELEMENTS) {
      return APIRenderers.ELEMENTS
    }
    return renderer ?? APIRenderers.ELEMENTS
  }, [renderer, isOas3_2, localRenderer])

  useEffect(() => {
    if (!rendererRef.current) { 
      return 
    }
    if (activeRenderer === APIRenderers.ELEMENTS) {
      rendererRef.current.scrollTo(0, 0)
    } else if (!isConsumerSide && hash === '#top') {
      rendererRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [hash, activeRenderer, isConsumerSide])

  const onToggle = (value: boolean) => {
    if (!isOas3_2) {
      setLocalRenderer(value ? APIRenderers.SWAGGER_UI : APIRenderers.ELEMENTS)
    }
  }

  return (
    <div id="top" ref={containerRef} className={clsx('flex flex-col sp-api-viewer', !isConsumerSide && 'h-full')}>
      {
        isMobile &&
        <BreadcrumbBar key={`breadcrumb-${pathname}`} />
      }
      {shouldRenderToolbar && (
        <div data-testid="api-viewer-toolbar" className={clsx('sp-api-viewer-toolbar flex top-0 justify-between w-full gap-1 px-6 md:px-9 xl:px-6 h-20 border-b border-b-(--sp-color-border-subtle) bg-(--sp-color-surface)', isConsumerSide && !isMobile && 'sticky z-40 border-l border-l-[rgba(128,128,128,0.35)]')}>
          {!isMobile && <div className="flex items-center gap-2">
            {shouldRenderRouting ?
              <RoutingSettingsButton routingSettings={routingSettings} />
              : null
            }
          </div>}
          <div className="flex items-center gap-2">
            {shouldRenderOpenApiRendererToggle &&
              <div className={clsx(!isOas3 && 'mr-2')}>
                <OpenAPIRendererToggle onToggle={onToggle} renderer={activeRenderer} isOas3_2={isOas3_2} />
              </div>}
            {
              !isMobile ?
                <>
                  {isOas3 ? <ExploreButton exploreUrl={exploreUrl} SHUBApiDetails={SHUBApiDetails} /> : null}
                  {shouldRenderCodeGen ?
                    <div className="sp-api-viewer-codegen-button">
                      <GenerateCodeDropdown
                        definition={definition as OpenAPI2Definition | OpenAPI3Definition}
                        generatorLanguages={generatorLanguages}
                        title={title}
                        productId={productId}
                        orgName={orgName}
                        path={path}
                        SHUBApiDetails={SHUBApiDetails}
                        flattenAvailable={portalFlattenActive || registryFlattenAvailable}
                        flattenFlow={flattenFlow}
                        tableOfContentsId={tableOfContentsId}
                        disabled={downloadDisabled}
                      />
                    </div>
                    : null
                  }
                  {shouldRenderDownload ?
                    <DownloadApiDropdown
                      title={title}
                      definition={definition}
                      disabled={downloadDisabled}
                    />
                    : null
                  }
                  {showEditButtons ?
                    <EditContentButtons
                      contentUrl={providerContentSlug}
                      shubUrl={shubUrl}
                    />
                    : null
                  }
                </> : null
            }
          </div>
        </div>
      )}

      <div className={clsx(
        'sp-api-viewer-content w-full bg-(--sp-color-surface)',
        !isConsumerSide && 'overflow-auto',
        !isConsumerSide && (shouldRenderToolbar ? 'h-[calc(100%_-_80px)]' : 'h-full'),
        isConsumerSide && '2xl:max-w-[calc(1300px+3rem)] mx-auto')} ref={rendererRef}>
        {
          isConsumerSide && !isMobileScreen && <div className="px-5 pt-6">
            <BreadcrumbBar key={`breadcrumb-${pathname}`} />
          </div>
        }
        <Suspense fallback={<div className="flex items-center justify-center w-full h-full"><PlainLoader /></div>}>
          <ApiRenderer
            activeRenderer={activeRenderer}
            definition={definition}
            hash={hash}
            ref={ref}
            isAdmin={isAdmin}
            pathname={pathname}
            isConsumerSide={isConsumerSide}
            lastModifiedDate={lastModifiedDate}
            exploreUrl={exploreUrl}
            SHUBApiDetails={SHUBApiDetails}
            oasVersion={oasVersion}
            routingSettings={routingSettings}
            ElementsRenderer={ElementsRenderer}
          />
        </Suspense>
      </div>
    </div>
  )
}